<template>
  <div
    :style="{ top: `${offset}px` }"
    class="zv-sidebar"
  >
    <slot v-if="showBackLink">
      <a
        :href="backLinkUrl"
        class="sidebar-item"
      >
        <icon name="chevron-left" />
        <span class="sidebar-item__label">{{ backLinkLabel }}</span>
      </a>
    </slot>
    <div
      v-show="anchors.length > 0"
      ref="anchors"
      class="zv-sidebar__wrapper"
    >
      <nav
        ref="nav"
        :class="{'is-fixed': anchorsFixed}"
        class="zv-sidebar__nav"
      >
        <ul
          ref="ul"
          class="unstyled-list"
        >
          <li
            v-for="anchor in anchors"
            :key="anchor.key"
            :class="{'is-active': anchor.key === activeAnchor}"
          >
            <a
              :class="{'active': anchor.key === activeAnchor}"
              class="sidebar-item"
              role="button"
              @click="onAnchorClick(anchor)"
            >
              <!-- eslint-disable vue/no-v-html -->
              <span
                class="sidebar-item__label"
                v-html="$options.filters && $options.filters.breakLastWord ? $options.filters.breakLastWord(anchor.name) : anchor.name"
              />
            </a>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script>
import throttle from 'lodash.throttle'
import ResizeObserver from 'resize-observer-polyfill'
import { Icon } from 'zv-components'

export default {
  name: 'ZvSidebar',
  components: { Icon },
  props: {
    affixSelector: {
      type: String,
      default: '[data-anchor]',
    },
    selector: {
      type: String,
      default: '[data-anchor]',
    },
    showBackLink: {
      type: Boolean,
      default: true,
    },
    backLinkLabel: {
      type: String,
      default: 'zurück',
    },
    backLinkUrl: {
      type: String,
      default: '#',
    },
    addBodyClass: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      anchors: [],
      activeAnchor: null,
      offset: 0,
      anchorsFixed: false,
    }
  },

  mounted () {
    this.getAnchors()
    this.setSidebarPosition()
    this.addResizeOberserver()

    if (this.anchors.length > 0) {
      this.addScrollListeners()
    }

    if (this.addBodyClass) {
      document.body.classList.add('has-sidebar')
    }
  },

  beforeDestory () {
    ['scroll'].map(e => {
      window.removeEventListener(e, this.onScroll)
    })

    this.resizeObserver && this.resizeObserver.disconnect()
    document.body && document.body.classList.remove('has-sidebar')
  },

  methods: {
    getAnchors () {
      const anchors = Array.from(document.querySelectorAll(this.selector))

      if (anchors && anchors.length > 0) {
        anchors.filter((anchor) => {
          this.anchors.push({
            key: anchor.getAttribute('data-anchor'),
            name: anchor.getAttribute('data-anchor-title'),
            node: anchor,
          })
        })
      }

      return this.anchors
    },

    setSidebarPosition () {
      let affixElement = document.querySelector(this.affixSelector)

      if (!affixElement) {
        affixElement = document.querySelector(this.selector)
      }

      this.offset = affixElement.offsetTop
    },

    addResizeOberserver () {
      this.resizeObserver = new ResizeObserver(() => {
        this.setSidebarPosition()
      })
      this.resizeObserver.observe(document.documentElement)
    },

    addScrollListeners () {
      ['scroll'].map(e => {
        window.addEventListener(e, throttle(e => this.onScroll(e)), { passive: true })
      })
    },

    onScroll () {
      this.activeAnchorBasedOnPosition()
      this.affixSidebar()
    },

    affixSidebar () {
      if (!this.anchorsFixed && window.scrollY >= this.$el.offsetTop) {
        this.anchorsFixed = true
      } else if (this.anchorsFixed && window.scrollY < this.$el.offsetTop) {
        this.anchorsFixed = false
      }
    },

    activeAnchorBasedOnPosition () {
      let activeKey = this.activeAnchor
      let touched = false
      let offsetTop = this.$refs.ul ? this.$refs.ul.offsetTop : 0

      this.anchors.map((anchor, index) => {
        const nextAnchor = this.anchors[index + 1]
        const offset = anchor.node.offsetTop - offsetTop

        if (offset <= window.scrollY && nextAnchor && (nextAnchor.node.offsetTop - offsetTop) >= window.scrollY) {
          activeKey = anchor.key
          touched = true
        } else if (!nextAnchor && offset <= window.scrollY) {
          activeKey = anchor.key
          touched = true
        }
      })

      if (!touched) {
        activeKey = null
      }

      this.activeAnchor = activeKey
    },

    onAnchorClick (anchor) {
      window.scrollTo({
        'top': anchor.node.offsetTop + 5,
        'behavior': 'smooth',
      })

      this.$emit('select', anchor)
    },
  },
}
</script>
